import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "../../utils/colorUtils";
import Select from "../../components/Select";
import { useGetChannels } from "../../api/channels/service";
import { useGenerateInventoryTaxReport } from "../../api/reports/service";
import vitInstructionsPdf from "../../assets/pdfs/report/TX_VITstatement_instructions.pdf";
import { useNavigate } from "react-router-dom";

const TxDealersInventoryTaxStatement = ({ activeColor = "blue", account }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const textColor = isLightTheme ? "#1d253b" : "#ffffff";
  const labelColor = isLightTheme ? "#525f7f" : "rgba(255, 255, 255, 0.8)";
  const cardBg = isLightTheme ? "#ffffff" : "#27293d";
  const subTextColor = isLightTheme ? "#525f7f" : "rgba(255, 255, 255, 0.6)";

  const { data: locationsData, isLoading: locationsLoading } = useGetChannels("sales_location_branch");
  const generateReportMutation = useGenerateInventoryTaxReport();

  const [pdfUrl, setPdfUrl] = useState(null);

  const [formData, setFormData] = useState(() => {
    const now = new Date();
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return {
      statementType: "Monthly Statement",
      month: monthNames[prevMonthDate.getMonth()],
      year: String(prevMonthDate.getFullYear()),
      vitAccountNumber: "",
      useCurrentVit: false,
      vitTaxRate: "",
      exempt: false,
      completedBy: account?.username || "",
      title: ""
    };
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSelectChange = (name, option) => {
    setFormData(prev => ({
      ...prev,
      [name]: option ? option.value : ""
    }));
  };

  const statementTypeOptions = [
    { value: "Annual Declaration", label: "Annual Declaration" },
    { value: "Monthly Statement", label: "Monthly Statement" },
  ];

  const monthOptions = [
    { value: "Jan", label: "Jan" },
    { value: "Feb", label: "Feb" },
    { value: "Mar", label: "Mar" },
    { value: "Apr", label: "Apr" },
    { value: "May", label: "May" },
    { value: "Jun", label: "Jun" },
    { value: "Jul", label: "Jul" },
    { value: "Aug", label: "Aug" },
    { value: "Sep", label: "Sep" },
    { value: "Oct", label: "Oct" },
    { value: "Nov", label: "Nov" },
    { value: "Dec", label: "Dec" },
  ];

  const yearOptions = [
    { value: "2006", label: "2006" },
    { value: "2007", label: "2007" },
    { value: "2008", label: "2008" },
    { value: "2009", label: "2009" },
    { value: "2010", label: "2010" },
    { value: "2011", label: "2011" },
    { value: "2012", label: "2012" },
    { value: "2013", label: "2013" },
    { value: "2014", label: "2014" },
    { value: "2015", label: "2015" },
    { value: "2016", label: "2016" },
    { value: "2017", label: "2017" },
    { value: "2018", label: "2018" },
    { value: "2019", label: "2019" },
    { value: "2020", label: "2020" },
    { value: "2021", label: "2021" },
    { value: "2022", label: "2022" },
    { value: "2023", label: "2023" },
    { value: "2024", label: "2024" },
    { value: "2025", label: "2025" },
    { value: "2026", label: "2026" },
    { value: "2027", label: "2027" },
  ];

  const vitAccountOptions = useMemo(() => {
    if (locationsData?.results && locationsData.results.length > 0) {
      return locationsData.results.map(location => ({
        value: String(location.id),
        label: location?.sales_location?.dealer_number + " - " + location?.name
      }));
    }
    return [{ value: "", label: "" }];
  }, [locationsData]);

  const currentVitTaxRate = useMemo(() => {
    if (!locationsData?.results) return "0.0000000000";

    const selectedLocation = locationsData.results.find(loc => String(loc.id) === String(formData.vitAccountNumber));
    if (selectedLocation?.sales_location?.taxes) {
      const inventoryTax = selectedLocation.sales_location.taxes.find(tax => tax.category === "Inventory Tax");
      if (inventoryTax?.tax_rate !== undefined && inventoryTax?.tax_rate !== null) {
        return (Number(inventoryTax.tax_rate) / 100).toFixed(10);
      }
    }
    return "0.0000000000";
  }, [locationsData, formData.vitAccountNumber]);

  useEffect(() => {
    setFormData(prev => {
      if (prev.useCurrentVit && prev.vitTaxRate !== currentVitTaxRate) {
        return { ...prev, vitTaxRate: currentVitTaxRate, exempt: false };
      }
      return prev;
    });
  }, [formData.useCurrentVit, currentVitTaxRate]);

  useEffect(() => {
    setFormData(prev => {
      if (prev.exempt && (prev.vitTaxRate !== "0" || prev.useCurrentVit)) {
        return { ...prev, vitTaxRate: "0", useCurrentVit: false };
      }
      return prev;
    });
  }, [formData.exempt]);

  useEffect(() => {
    if (account?.username && !formData.completedBy) {
      setFormData(prev => ({
        ...prev,
        completedBy: account.username
      }));
    }
  }, [account]);

  useEffect(() => {
    if (locationsData?.results?.length > 0 && !formData.vitAccountNumber) {
      setFormData(prev => ({
        ...prev,
        vitAccountNumber: String(locationsData.results[0].id)
      }));
    }
  }, [locationsData]);

  const handleGenerate = async () => {
    try {
      const selectedLocation = locationsData?.results?.find(loc => String(loc.id) === String(formData.vitAccountNumber));

      const payload = {
        ...formData,
        sales_location_id: selectedLocation?.sales_location?.id || formData.vitAccountNumber,
      };

      const result = await generateReportMutation.mutateAsync(payload);
      if (result) {
        const blobUrl = URL.createObjectURL(new Blob([result], { type: 'application/pdf' }));
        setPdfUrl(blobUrl);
      }
    } catch (e) {
      console.error("Failed to generate report", e);
    }
  };

  const isGenerateDisabled = generateReportMutation.isPending ||
    !formData.vitAccountNumber ||
    formData.vitTaxRate === "" ||
    formData.vitTaxRate === null;

  if (pdfUrl) {
    return (
      <div className="content">
        <Card className="shadow-sm" style={{ border: "none", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '15px 25px', backgroundColor: '#fcfcfc', borderBottom: '1px solid #eee' }}>
            <Button
              size="sm"
              color="secondary"
              onClick={() => setPdfUrl(null)}
              className="mb-0"
              style={{ padding: "6px 12px", display: 'flex', alignItems: 'center', boxShadow: 'none' }}
            >
              <i className="tim-icons icon-minimal-left" style={{ marginRight: "6px", fontSize: "10px" }} />
              {t('common.back') || 'Back'}
            </Button>
            <h4 style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: "#1d253b", letterSpacing: "0.5px", marginLeft: "20px", textTransform: "uppercase" }}>
              DOCUMENT VIEWER
            </h4>
          </div>
          <CardBody style={{ padding: "15px 25px", height: "82vh", minHeight: "600px", backgroundColor: "#fcfcfc" }}>
            <iframe src={pdfUrl} width="100%" height="100%" style={{ border: "none", borderRadius: "4px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }} title="VIT Statement PDF" />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="content">
      <Card className="mb-4 shadow-sm" style={{ border: "none", borderRadius: "8px", backgroundColor: cardBg }}>
        <CardBody style={{ padding: "20px 25px" }}>
          {locationsLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: activeColorStyle.borderColor }}
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                <Button
                  size="sm"
                  color="secondary"
                  onClick={() => navigate(-1)}
                  style={{
                    borderRadius: "4px",
                    padding: "4px 12px",
                    fontSize: "11px",
                    margin: "0 15px 0 0",
                    boxShadow: "none",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <i className="tim-icons icon-minimal-left" style={{ marginRight: "6px", fontSize: "10px" }} />
                  BACK
                </Button>
                <h5 style={{ fontWeight: "700", fontSize: "14px", textTransform: "uppercase", margin: 0, color: textColor, letterSpacing: "0.5px" }}>
                  TX - DEALER'S MOTOR VEHICLE INVENTORY TAX STATEMENT
                </h5>
              </div>
              <Row>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Statement Type:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select w-100"
                      options={statementTypeOptions}
                      value={statementTypeOptions.find(o => o.value === formData.statementType) || null}
                      onChange={(option) => handleSelectChange("statementType", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Month:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select w-100"
                      options={monthOptions}
                      value={monthOptions.find(o => o.value === formData.month) || null}
                      onChange={(option) => handleSelectChange("month", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Year:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select w-100"
                      options={yearOptions}
                      value={yearOptions.find(o => o.value === formData.year) || null}
                      onChange={(option) => handleSelectChange("year", option)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>VIT Account Number:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select w-100"
                      options={vitAccountOptions}
                      isLoading={locationsLoading}
                      value={vitAccountOptions.find(o => String(o.value) === String(formData.vitAccountNumber)) || null}
                      onChange={(option) => handleSelectChange("vitAccountNumber", option)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'flex-start' }}>
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Current VIT Tax Rate for Selected Account:</Label>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: "4px" }}>
                      <span style={{ fontWeight: "800", fontSize: "13px", marginRight: "30px" }}>{currentVitTaxRate}</span>
                      <FormGroup check className="mb-0" style={{ paddingLeft: "1.25rem", marginTop: 0 }}>
                        <Label check style={{ fontWeight: "600", fontSize: "12px", color: labelColor }}>
                          <Input type="checkbox" name="useCurrentVit" checked={formData.useCurrentVit} onChange={handleChange} style={{ marginTop: "2px" }} />
                          <span className="form-check-sign" />
                          Use Current VIT
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
                <Col md="4" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>VIT Tax Rate:</Label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Input type="text" name="vitTaxRate" disabled={formData.useCurrentVit || formData.exempt} value={formData.vitTaxRate} onChange={handleChange} style={{ height: "32px", padding: "4px 8px", fontSize: "12px", borderRadius: "4px", flex: 1, marginRight: "30px", opacity: (formData.useCurrentVit || formData.exempt) ? 0.6 : 1 }} />
                      <FormGroup check className="mb-0" style={{ paddingLeft: "1.25rem", margin: 0, minWidth: "100px" }}>
                        <Label check style={{ fontWeight: "600", fontSize: "12px", color: labelColor }}>
                          <Input type="checkbox" name="exempt" checked={formData.exempt} onChange={handleChange} style={{ marginTop: "2px" }} />
                          <span className="form-check-sign" />
                          Exempt
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="4" className="mb-3 mb-md-0">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Completed By:</Label>
                    <Input type="text" name="completedBy" value={formData.completedBy} onChange={handleChange} style={{ height: "32px", padding: "4px 8px", fontSize: "12px", borderRadius: "4px" }} />
                  </FormGroup>
                </Col>
                <Col md="4" className="mb-3 mb-md-0">
                  <FormGroup className="mb-0">
                    <Label style={{ fontWeight: "600", fontSize: "11px", marginBottom: "4px", color: labelColor }}>Title:</Label>
                    <Input type="text" name="title" value={formData.title} onChange={handleChange} style={{ height: "32px", padding: "4px 8px", fontSize: "12px", borderRadius: "4px" }} />
                  </FormGroup>
                </Col>
                <Col md="4" className="d-flex align-items-end justify-content-end pr-3">
                  <Button size="sm" disabled={isGenerateDisabled} style={{ background: "#2dce89", borderColor: "#2dce89", color: "#fff", fontWeight: "700", fontSize: "12px", padding: "8px 24px", letterSpacing: "0.5px", margin: 0, backgroundImage: "none", opacity: isGenerateDisabled ? 0.6 : 1, cursor: isGenerateDisabled ? "not-allowed" : "pointer", borderRadius: "4px", boxShadow: "none" }} onClick={handleGenerate}>
                    {generateReportMutation.isPending ? t('common.loading') || 'GENERATING...' : 'GENERATE STATEMENT'}
                  </Button>
                </Col>
              </Row>
              <hr style={{ borderTop: "1px solid #eee", margin: "30px 0 20px 0" }} />

              <div style={{ padding: "0 5px" }}>
                <h5 style={{ fontWeight: "700", fontSize: "13px", marginBottom: "15px", color: textColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>TEXAS - VIT INSTRUCTIONS</h5>
                <ol style={{ fontSize: "12px", color: subTextColor, paddingLeft: "15px", lineHeight: "1.6", margin: 0 }}>
                  <li className="mb-2">The statement only pulls <strong>"Contracted"</strong> deals that have a Contract date within the selected Month/Year date range.</li>
                  <li className="mb-2">Select the <strong>Month</strong> and <strong>Year</strong></li>
                  <li className="mb-2">Choose the <strong>VIT Account Number</strong> you are reporting for. <strong>The VIT Account Number</strong> comes from the Taxes/Fees info tab on your Sales Location/Branch channel. Once you select the Account Number, the VIT Tax rate for that location will appear. If the account number dropdown says "Not Defined-", that indicates that your sales location does not have an account number. Print a VIT statement for each VIT account number.</li>
                  <li className="mb-2">Enter the <strong>VIT Tax Rate.</strong> Select "Use Current VIT" if the rate displayed is correct for the month reporting. Otherwise enter the correct rate into the box or choose "Exempt". <strong>"Exempt" should only be chosen if the dealership is in the first year of business and has not yet been assigned a rate.</strong></li>
                  <li className="mb-2">Enter a Completed By and Title. <i>(not required)</i></li>
                  <li className="mb-2">Click the Generate Statement button and the statement will appear.</li>
                </ol>
                <hr style={{ borderTop: "1px solid #eee", margin: "20px 0" }} />
                <div className="mt-2">
                  <a href={vitInstructionsPdf} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px", fontWeight: "600", color: activeColorStyle.borderColor || "#2196f3", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                    <i className="tim-icons icon-paper" style={{ marginRight: "8px" }} />
                    Download VIT Instructions (.pdf)
                  </a>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default TxDealersInventoryTaxStatement;
